import { slice, pipe } from 'ramda'
import { withPrefix } from 'gatsby'
import { ProductItemType } from '~/@types/models'
import { useState, useCallback, useEffect } from 'react'
import { debounce, isEmpty, isEqual } from 'lodash'
import { set } from 'store'
type FilerType = {
  inStockOnly?: boolean
  price?: any
  selectedFilters?: Record<
    string,
    Array<{
      name: string
      value: string
    }>
  >
}

type Props<T> = {
  initialData?: readonly T[]
  initialPage?: number
  perPage?: number
  total: number
  collectionHandle: string
  filter?: FilerType
}

type UsePaginatedCollection<T> = {
  currentPage: number
  data: readonly T[]
  loadMore: () => Promise<void>
  hasMore: boolean
  getData: () => Promise<void>
  isFetching: boolean
  currentFilters: FilerType | undefined
  resetFilters: () => void
  setFilters: (filters: FilerType) => void
  setCurrentFilters: React.Dispatch<React.SetStateAction<FilerType | undefined>>
}

const api = (collectionHandle: string) => withPrefix(`/collection-data/${collectionHandle}.json`)

const filterIsStockOnly = (product: ProductItemType) =>
  product.variants.find(variant => variant.availableForSale === true)

const filterByMetafield = (product: ProductItemType, filterOptions: any[], metafield: string) => {
  let found
  filterOptions.forEach((option: any) => {
    if (product.metafields[metafield] && product.metafields[metafield].includes(option.value)) {
      found = product
    }
    return
  })
  return found
}

const usePaginatedCollection = <T extends ProductItemType>({
  initialPage = 1,
  total,
  perPage = 20,
  initialData = [],
  collectionHandle,
}: Props<T>): UsePaginatedCollection<T> => {
  const [page, setPage] = useState<number>(initialPage + 1)
  const [data, setData] = useState<readonly T[]>([])
  const [newTotal, setNewTotal] = useState<number>(total)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [currentFilters, setCurrentFilters] = useState<FilerType | undefined>({})
  const [inStockOnly, setInStockOnly] = useState<boolean>(true)

  useEffect(() => {
    setInStockOnly(currentFilters?.inStockOnly || false)
  }, [])

  const filterData = (productsData: any[]) => {
    const selectedFilters = () => {
      const filters: string[] = []
      if (currentFilters?.selectedFilters) {
        Object.keys(currentFilters.selectedFilters).forEach((option: any) => {
          filters.push(option)
        })
      }
      return filters
    }

    const isolateCategoryName = (categoryName: string) => {
      const foundCategory = currentFilters?.selectedFilters
        ? currentFilters?.selectedFilters[categoryName]
        : []

      if (foundCategory) {
        return foundCategory
      }
    }

    if (currentFilters && currentFilters.selectedFilters) {
      const selectedOptions = selectedFilters()
      let filteredData = productsData

      const dataFilter = (products: any[], option: string) =>
        products.filter((product: ProductItemType) => {
          if (currentFilters.selectedFilters) {
            const valuesArray = isolateCategoryName(option) || []
            return pipe(filterByMetafield)(product, valuesArray, option)
          }
          return product
        })

      selectedOptions.forEach(option => {
        filteredData = dataFilter(filteredData, option)
      })
      return filteredData
    }
  }

  const fetchData = async (collectionHandle: string, filter: any) => {
    const res = await fetch(api(collectionHandle))
    const json = await res.json()
    const productsData = json?.data ? json.data : []

    if (!isEmpty(filter)) {
      return filterData(productsData)
    }

    return productsData
  }

  const getData = async () => {
    try {
      const productsData = await fetchData(collectionHandle, currentFilters)

      setNewTotal(productsData.length)
      const newProducts = pipe((collection: T[]) => {
        if (inStockOnly) {
          return collection.filter(product => {
            return pipe(filterIsStockOnly)(product)
          })
        }
        return collection
      }, slice(0, (page + 1) * perPage))(productsData) as readonly T[]

      setPage(page => page + 1)
      setData(newProducts)
      setTimeout(() => {
        setIsFetching(false)
      }, 500)
    } catch (e) {
      // todo
      setIsFetching(false)
    }
  }

  const loadMore = useCallback(async () => {
    await getData()
  }, [getData])

  useEffect(() => {
    if (currentFilters && !isEmpty(currentFilters.selectedFilters)) {
      setData([])
      setIsFetching(true)
      getData()
    } else if (initialData.length > 0 && !isFetching && !isEqual(data, initialData)) {
      setData(initialData)
    }
  }, [currentFilters])

  return {
    currentPage: initialPage,
    data,
    loadMore,
    getData,
    hasMore: (page - 1) * perPage < newTotal,
    isFetching,
    currentFilters,
    resetFilters: () => {
      setCurrentFilters(state => {
        if (state) {
          delete state.selectedFilters
        }
        return state
      })
      setData(initialData)
      setPage(1)
    },
    setFilters: (filters: FilerType) => {
      setCurrentFilters(filters)
    },
    setCurrentFilters,
  }
}

export default usePaginatedCollection
